import { z } from 'zod';
import { StockMovementType, PurchaseStatus, PaymentMethod } from '../enums.js';
import { paginationQuerySchema } from '../pagination.js';
import { optionalString, optionalEmail, optionalId, optionalInt } from './common.js';
// ------------------------------------------------------------
//  Catégories
// ------------------------------------------------------------
export const createCategorySchema = z.object({
    name: z.string().trim().min(1, 'Nom requis').max(100),
    description: optionalString(500),
    parentId: optionalId(),
});
export const updateCategorySchema = createCategorySchema.partial();
// ------------------------------------------------------------
//  Pièces détachées
// ------------------------------------------------------------
export const createSparePartSchema = z.object({
    reference: z.string().trim().min(1, 'Référence requise').max(60),
    barcode: optionalString(60),
    name: z.string().trim().min(1, 'Désignation requise').max(200),
    brand: optionalString(60),
    categoryId: optionalId(),
    supplierId: optionalId(),
    purchasePriceCents: z.coerce.number().int().min(0).default(0),
    salePriceCents: z.coerce.number().int().min(0).default(0),
    taxRate: z.coerce.number().min(0).max(100).default(0),
    quantity: z.coerce.number().int().min(0).default(0), // stock initial
    minStock: z.coerce.number().int().min(0).default(0),
    location: optionalString(60),
    // Image compressée (data URL). Cap large : la compression navigateur garde ~30-60 Ko.
    imageUrl: z.string().max(2_000_000).optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
});
// La quantité n'est PAS modifiable directement : elle évolue via les mouvements.
export const updateSparePartSchema = createSparePartSchema.omit({ quantity: true }).partial();
// Import CSV : une ligne = une pièce (catégorie/fournisseur par NOM).
export const importSparePartRowSchema = z.object({
    reference: z.string().trim().min(1, 'Référence requise').max(60),
    name: z.string().trim().min(1, 'Désignation requise').max(200),
    brand: optionalString(60),
    category: optionalString(120),
    supplier: optionalString(120),
    barcode: optionalString(60),
    purchasePriceCents: z.coerce.number().int().min(0).default(0),
    salePriceCents: z.coerce.number().int().min(0).default(0),
    taxRate: z.coerce.number().min(0).max(100).default(0),
    quantity: z.coerce.number().int().min(0).default(0),
    minStock: z.coerce.number().int().min(0).default(0),
    location: optionalString(60),
});
export const importSparePartsSchema = z.object({
    rows: z.array(importSparePartRowSchema).min(1, 'Aucune ligne à importer').max(5000),
});
export const sparePartsQuerySchema = paginationQuerySchema.extend({
    categoryId: z.string().optional(),
    supplierId: z.string().optional(),
    lowStock: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((v) => v === true || v === 'true'),
});
// ------------------------------------------------------------
//  Mouvements de stock
// ------------------------------------------------------------
export const createStockMovementSchema = z.object({
    sparePartId: z.string().min(1),
    type: z.nativeEnum(StockMovementType),
    // IN/OUT : quantité (>0). ADJUSTMENT : nouvelle quantité absolue (>=0).
    quantity: z.coerce.number().int().min(0),
    reason: optionalString(255),
    unitCostCents: optionalInt({ min: 0 }),
});
export const stockMovementsQuerySchema = paginationQuerySchema.extend({
    sparePartId: z.string().optional(),
    type: z.nativeEnum(StockMovementType).optional(),
});
// ------------------------------------------------------------
//  Fournisseurs
// ------------------------------------------------------------
export const createSupplierSchema = z.object({
    name: z.string().trim().min(1, 'Nom requis').max(150),
    contactName: optionalString(120),
    phone: optionalString(30),
    email: optionalEmail(),
    address: optionalString(255),
    notes: optionalString(2000),
});
export const updateSupplierSchema = createSupplierSchema.partial();
/** Règlement à un fournisseur (éventuellement rattaché à une commande). */
export const createSupplierPaymentSchema = z.object({
    amountCents: z.coerce.number().int().min(1),
    method: z.nativeEnum(PaymentMethod),
    reference: optionalString(100),
    purchaseId: optionalId(),
    paidAt: z.coerce.date().optional(),
});
// ------------------------------------------------------------
//  Achats (commandes fournisseurs)
// ------------------------------------------------------------
export const purchaseItemSchema = z.object({
    sparePartId: z.string().min(1, 'Pièce requise'),
    quantityOrdered: z.coerce.number().int().min(1),
    unitCostCents: z.coerce.number().int().min(0),
    taxRate: z.coerce.number().min(0).max(100).default(0),
});
export const createPurchaseSchema = z.object({
    supplierId: z.string().min(1, 'Fournisseur requis'),
    expectedAt: z.coerce.date().optional(),
    notes: optionalString(2000),
    status: z.nativeEnum(PurchaseStatus).default(PurchaseStatus.DRAFT),
    items: z.array(purchaseItemSchema).min(1, 'Au moins une ligne'),
});
export const updatePurchaseSchema = z.object({
    supplierId: z.string().min(1).optional(),
    expectedAt: z.coerce.date().optional(),
    notes: optionalString(2000),
    status: z.nativeEnum(PurchaseStatus).optional(),
    items: z.array(purchaseItemSchema).optional(),
});
/** Réception : quantités reçues par ligne de commande. */
export const receivePurchaseSchema = z.object({
    items: z
        .array(z.object({
        purchaseItemId: z.string().min(1),
        quantityReceived: z.coerce.number().int().min(0),
    }))
        .min(1),
});
export const purchasesQuerySchema = paginationQuerySchema.extend({
    status: z.nativeEnum(PurchaseStatus).optional(),
    supplierId: z.string().optional(),
});
//# sourceMappingURL=inventory.js.map